export type SocietyRegistrationStatus = 'REGISTERED' | 'UNREGISTERED';
export type SocietyVerificationStatus = 'VERIFIED' | 'UNVERIFIED';

export interface SocietyStatusInfo {
  registrationStatus: SocietyRegistrationStatus;
  verificationStatus: SocietyVerificationStatus;
  isRegistered: boolean;
  isVerified: boolean;
  registrationNumber?: string;
  verifiedAt?: string | null;
  verificationNotes?: string;
}

/**
 * Normalizes society/tenant/prospect objects into canonical registration & verification statuses.
 * Preserves independent state representation without altering underlying data.
 */
export function resolveSocietyStatus(society?: {
  registrationStatus?: string | null;
  verificationStatus?: string | null;
  isVerified?: boolean | null;
  verifiedAt?: string | null;
  status?: string | null;
  registrationNumber?: string | null;
  _id?: string;
  id?: string;
  settings?: Record<string, unknown>;
  [key: string]: unknown;
} | null): SocietyStatusInfo {
  if (!society) {
    return {
      registrationStatus: 'UNREGISTERED',
      verificationStatus: 'UNVERIFIED',
      isRegistered: false,
      isVerified: false,
    };
  }

  // 1. Determine Registration Status
  let registrationStatus: SocietyRegistrationStatus = 'REGISTERED';
  if (society.registrationStatus) {
    registrationStatus =
      String(society.registrationStatus).toUpperCase() === 'UNREGISTERED'
        ? 'UNREGISTERED'
        : 'REGISTERED';
  } else if (society.status === 'unregistered' || society.status === 'PROSPECT') {
    registrationStatus = 'UNREGISTERED';
  } else if (!society._id && !society.id) {
    registrationStatus = 'UNREGISTERED';
  }

  // 2. Determine Verification Status
  let verificationStatus: SocietyVerificationStatus = 'UNVERIFIED';
  if (society.verificationStatus) {
    verificationStatus =
      String(society.verificationStatus).toUpperCase() === 'VERIFIED'
        ? 'VERIFIED'
        : 'UNVERIFIED';
  } else if (society.isVerified === true || society.settings?.isVerified === true) {
    verificationStatus = 'VERIFIED';
  } else if (
    society.status === 'VERIFIED' ||
    society.status === 'ACTIVE' ||
    society.verifiedAt
  ) {
    // If backend marks verification date or verified status
    verificationStatus = society.verifiedAt ? 'VERIFIED' : 'UNVERIFIED';
  }

  return {
    registrationStatus,
    verificationStatus,
    isRegistered: registrationStatus === 'REGISTERED',
    isVerified: verificationStatus === 'VERIFIED',
    registrationNumber: society.registrationNumber || undefined,
    verifiedAt: society.verifiedAt || null,
    verificationNotes: typeof society.verificationNotes === 'string' ? society.verificationNotes : undefined,
  };
}
